import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
  View,
  FlatList,
  StyleSheet,
  RefreshControl,
  TouchableOpacity,
} from 'react-native';
import {
  Text,
  Searchbar,
  Card,
  FAB,
  ActivityIndicator,
  Appbar,
  Snackbar,
} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation, DrawerActions} from '@react-navigation/native';
import {useCustomerStore} from '../../store/useCustomerStore';
import {colors, spacing} from '../../theme';

const CustomerListScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const {
    customers,
    isLoading,
    pagination,
    fetchCustomers,
    searchCustomers,
  } = useCustomerStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState('');
  const debounceRef = useRef(null);

  useEffect(() => {
    fetchCustomers({page: 0, size: 20});
  }, [fetchCustomers]);

  const handleSearch = useCallback(
    query => {
      setSearchQuery(query);

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(async () => {
        try {
          if (query.trim().length > 0) {
            await searchCustomers(query.trim());
          } else {
            await fetchCustomers({page: 0, size: 20});
          }
        } catch (e) {
          setSnackbar(e.response?.data?.message || e.message || t('common.error'));
        }
      }, 300);
    },
    [searchCustomers, fetchCustomers, t],
  );

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      if (searchQuery.trim().length > 0) {
        await searchCustomers(searchQuery.trim());
      } else {
        await fetchCustomers({page: 0, size: 20});
      }
    } finally {
      setRefreshing(false);
    }
  }, [fetchCustomers, searchCustomers, searchQuery]);

  const handleLoadMore = useCallback(() => {
    if (isLoading || searchQuery.trim().length > 0) {
      return;
    }
    const nextPage = pagination.page + 1;
    if (nextPage < pagination.totalPages) {
      fetchCustomers({page: nextPage, size: pagination.size});
    }
  }, [isLoading, pagination, fetchCustomers, searchQuery]);

  const handleCustomerPress = useCallback(
    customerId => {
      navigation.navigate('CustomerDetail', {customerId});
    },
    [navigation],
  );

  const handleAddCustomer = useCallback(() => {
    navigation.navigate('CustomerForm');
  }, [navigation]);

  const renderCustomerCard = useCallback(
    ({item}) => (
      <TouchableOpacity
        activeOpacity={0.7}
        onPress={() => handleCustomerPress(item.id)}>
        <Card style={styles.card}>
          <Card.Content style={styles.cardContent}>
            <View style={styles.cardHeader}>
              <View style={styles.avatar}>
                <Text style={styles.avatarText}>
                  {(item.fullName || '?').charAt(0).toUpperCase()}
                </Text>
              </View>
              <View style={styles.cardInfo}>
                <Text variant="titleMedium" style={styles.customerName}>
                  {item.fullName}
                </Text>
                {item.phone ? (
                  <Text variant="bodyMedium" style={styles.customerPhone}>
                    {item.phone}
                  </Text>
                ) : null}
                {item.city ? (
                  <Text variant="bodySmall" style={styles.customerCity}>
                    {item.city}
                  </Text>
                ) : null}
              </View>
            </View>
          </Card.Content>
        </Card>
      </TouchableOpacity>
    ),
    [handleCustomerPress],
  );

  const renderEmpty = useCallback(() => {
    if (isLoading) {
      return null;
    }
    return (
      <View style={styles.emptyContainer}>
        <Text variant="titleMedium" style={styles.emptyTitle}>
          {searchQuery.trim().length > 0
            ? t('common.noResults') || 'No results found'
            : t('common.noData') || 'No customers yet'}
        </Text>
        <Text variant="bodyMedium" style={styles.emptySubtitle}>
          {searchQuery.trim().length > 0
            ? (t('common.tryDifferentSearch') || 'Try a different search term')
            : (t('common.tapToAdd') || 'Tap + to add your first customer')}
        </Text>
      </View>
    );
  }, [isLoading, searchQuery, t]);

  const renderFooter = useCallback(() => {
    if (!isLoading || customers.length === 0) {
      return null;
    }
    return (
      <View style={styles.footer}>
        <ActivityIndicator size="small" color={colors.primary} />
      </View>
    );
  }, [isLoading, customers.length]);

  const keyExtractor = useCallback(item => String(item.id), []);

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.Action icon="menu" onPress={() => navigation.dispatch(DrawerActions.openDrawer())} />
        <Appbar.Content title={t('customer.title')} />
      </Appbar.Header>

      <View style={styles.searchContainer}>
        <Searchbar
          placeholder={t('customer.searchPlaceholder')}
          value={searchQuery}
          onChangeText={handleSearch}
          style={styles.searchbar}
          inputStyle={styles.searchInput}
          iconColor={colors.textSecondary}
        />
      </View>

      {isLoading && customers.length === 0 ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      ) : (
        <FlatList
          data={customers}
          renderItem={renderCustomerCard}
          keyExtractor={keyExtractor}
          contentContainerStyle={styles.listContent}
          ListEmptyComponent={renderEmpty}
          ListFooterComponent={renderFooter}
          onEndReached={handleLoadMore}
          onEndReachedThreshold={0.3}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={handleRefresh}
              colors={[colors.primary]}
              tintColor={colors.primary}
            />
          }
        />
      )}

      <FAB
        icon="plus"
        style={styles.fab}
        onPress={handleAddCustomer}
        color={colors.white}
      />

      <Snackbar
        visible={!!snackbar}
        onDismiss={() => setSnackbar('')}
        duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  appbar: {
    backgroundColor: colors.primary,
  },
  searchContainer: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    backgroundColor: colors.surface,
    elevation: 2,
  },
  searchbar: {
    backgroundColor: colors.background,
    elevation: 0,
  },
  searchInput: {
    fontSize: 14,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  listContent: {
    paddingHorizontal: spacing.md,
    paddingTop: spacing.sm,
    paddingBottom: 80,
  },
  card: {
    marginBottom: spacing.sm,
    backgroundColor: colors.surface,
    borderRadius: 8,
  },
  cardContent: {
    paddingVertical: spacing.sm,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  avatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.md,
  },
  avatarText: {
    color: colors.white,
    fontSize: 20,
    fontWeight: 'bold',
  },
  cardInfo: {
    flex: 1,
  },
  customerName: {
    fontWeight: '600',
    color: colors.text,
  },
  customerPhone: {
    color: colors.textSecondary,
    marginTop: 2,
  },
  customerCity: {
    color: colors.textSecondary,
    marginTop: 2,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: spacing.xxl * 2,
  },
  emptyTitle: {
    color: colors.text,
    marginBottom: spacing.sm,
  },
  emptySubtitle: {
    color: colors.textSecondary,
    textAlign: 'center',
  },
  footer: {
    paddingVertical: spacing.md,
    alignItems: 'center',
  },
  fab: {
    position: 'absolute',
    right: spacing.md,
    bottom: spacing.md,
    backgroundColor: colors.primary,
  },
});

export default CustomerListScreen;
